import childImg   from '../assets/img/child.avif';
import habitsImg  from '../assets/img/habits.jpg';
import parentsImg from '../assets/img/parents.jpg';
import img20      from '../assets/img/20.jpg';
import img2       from '../assets/img/2.jpg';
import img12      from '../assets/img/12.jpg';

export const blogs = [
  {
    id: 1,
    slug: 'importance-of-early-childhood-education',
    tag: 'Education',
    tagColor: '#e0f2fe',
    tagTextColor: '#0369a1',
    image: childImg,
    title: 'Importance of Early Childhood Education',
    excerpt: 'Early education builds strong foundations for learning, confidence, communication, and social skills in young children.',
    date: 'March 10, 2025',
    readTime: '5 min read',
    author: 'Kids Cosmos Team',
    content: [
      {
        type: 'intro',
        text: 'The first five years of a child\'s life are the most critical period for brain development. During this time, children form nearly 90% of their brain connections — making early childhood education one of the most powerful investments a family can make.'
      },
      {
        type: 'heading',
        text: 'Why Early Education Matters'
      },
      {
        type: 'paragraph',
        text: 'Early childhood education (ECE) is far more than just teaching children the alphabet or counting numbers. It is about building the whole child — emotionally, socially, physically, and intellectually. Children who attend quality early learning programs are more likely to succeed in school, develop positive relationships, and become contributing members of society.'
      },
      {
        type: 'paragraph',
        text: 'At Kids Cosmos School, we believe every child deserves a joyful start. Our Playgroup to Class 4 curriculum is carefully designed to nurture curiosity, creativity, and confidence from the very beginning.'
      },
      {
        type: 'heading',
        text: 'Key Benefits of Early Childhood Education'
      },
      {
        type: 'list',
        items: [
          '🧠 Brain Development: Structured play and learning activities stimulate neural connections that shape intelligence, memory, and problem-solving.',
          '💬 Language & Communication: Children learn to express themselves, listen actively, and build vocabulary through stories, songs, and guided conversations.',
          '🤝 Social Skills: Learning to share, take turns, cooperate, and resolve conflicts — essential life skills developed through play and group activities.',
          '😊 Emotional Confidence: A safe, nurturing environment helps children develop self-esteem and emotional resilience.',
          '📚 Love for Learning: Children who enjoy school early are more motivated, curious, and engaged throughout their entire academic journey.'
        ]
      },
      {
        type: 'heading',
        text: 'The Role of Play in Learning'
      },
      {
        type: 'paragraph',
        text: 'Play is the language of children. It is through play that young learners explore the world, test ideas, and develop critical thinking. At Kids Cosmos School, our play-based learning approach integrates structured and free play to ensure every child learns while having fun. Whether it\'s building blocks, dramatic role-play, or outdoor exploration — every activity is an opportunity to grow.'
      },
      {
        type: 'quote',
        text: '"Play is the highest form of research." — Albert Einstein'
      },
      {
        type: 'heading',
        text: 'What Parents Can Do at Home'
      },
      {
        type: 'paragraph',
        text: 'Parents are a child\'s first and most important teachers. Simple everyday activities like reading bedtime stories, singing nursery rhymes, asking open-ended questions, and encouraging creative play can have a profound impact on early development.'
      },
      {
        type: 'list',
        items: [
          '📖 Read together for at least 15 minutes every day.',
          '🎨 Provide crayons, clay, and craft materials for creative expression.',
          '🌿 Take nature walks and talk about what you observe.',
          '🎵 Sing songs and nursery rhymes to build language skills.',
          '💡 Ask "why" and "what do you think?" to encourage critical thinking.'
        ]
      },
      {
        type: 'paragraph',
        text: 'At Kids Cosmos School, we partner with parents to ensure that learning continues beyond school walls. Together, we give every child the strongest possible start in life. 🌟'
      }
    ]
  },
  {
    id: 2,
    slug: 'developing-healthy-habits-in-children',
    tag: 'Health',
    tagColor: '#dcfce7',
    tagTextColor: '#15803d',
    image: habitsImg,
    title: 'Developing Healthy Habits in Children',
    excerpt: 'Simple daily routines like proper sleep, nutrition, and playtime help children grow healthier and happier.',
    date: 'March 18, 2025',
    readTime: '4 min read',
    author: 'Kids Cosmos Team',
    content: [
      {
        type: 'intro',
        text: 'Healthy habits formed in childhood last a lifetime. When children learn to eat well, sleep properly, stay active, and practice good hygiene from an early age, they grow into healthier, happier, and more focused individuals.'
      },
      {
        type: 'heading',
        text: 'Why Habits Start Young'
      },
      {
        type: 'paragraph',
        text: 'Children\'s brains are incredibly receptive during the early years. Habits — both good and bad — become deeply ingrained when formed early. This is why establishing positive daily routines before the age of 7 is so powerful. At Kids Cosmos School, our daily schedule is designed to reinforce healthy patterns in a fun and consistent way.'
      },
      {
        type: 'heading',
        text: 'The Big Five: Healthy Habits for Children'
      },
      {
        type: 'list',
        items: [
          '🍎 Nutritious Eating: A balanced diet rich in fruits, vegetables, whole grains, and protein supports brain development and physical growth. Limit sugary snacks and encourage water over sugary drinks.',
          '😴 Quality Sleep: Children aged 3–6 need 10–13 hours of sleep daily. Consistent bedtime routines improve mood, memory, and learning ability significantly.',
          '🏃 Daily Physical Activity: At least 60 minutes of active play per day strengthens muscles, improves coordination, and boosts mental health.',
          '🧼 Personal Hygiene: Handwashing, brushing teeth, and bathing regularly protect children from illness and teach self-care and responsibility.',
          '📵 Balanced Screen Time: Limit screens to 1–2 hours per day for young children. Prioritize real-world play, books, and social interaction instead.'
        ]
      },
      {
        type: 'heading',
        text: 'Making Healthy Habits Fun'
      },
      {
        type: 'paragraph',
        text: 'Children are more likely to embrace healthy habits when they are made fun and engaging. Turn brushing teeth into a two-minute dance party. Make fruit and vegetable arrangements into colorful art. Create a "bedtime ritual" with a story, a song, and a hug. At Kids Cosmos School, our teachers incorporate health education into daily activities through games, songs, and hands-on projects.'
      },
      {
        type: 'quote',
        text: '"Take care of your body. It\'s the only place you have to live." — Jim Rohn'
      },
      {
        type: 'heading',
        text: 'Role of Consistency and Routine'
      },
      {
        type: 'paragraph',
        text: 'Consistency is the key to habit formation. Children thrive when they know what to expect. A predictable daily routine — wake up, breakfast, school, play, dinner, story, sleep — reduces anxiety and helps children feel safe and in control. When routines are positive and health-focused, children naturally internalize those values.'
      },
      {
        type: 'paragraph',
        text: 'Start small. Choose one healthy habit to focus on each month. Celebrate small victories. Be a role model. Children learn by watching the adults around them. When parents and teachers model healthy behaviors together, children follow naturally. 💪'
      }
    ]
  },
  {
    id: 3,
    slug: 'role-of-parents-and-teachers-together',
    tag: 'Community',
    tagColor: '#fef9c3',
    tagTextColor: '#a16207',
    image: parentsImg,
    title: 'Role of Parents & Teachers Together',
    excerpt: 'When parents and teachers work together, children feel more secure, motivated, and confident in their learning journey.',
    date: 'March 25, 2025',
    readTime: '5 min read',
    author: 'Kids Cosmos Team',
    content: [
      {
        type: 'intro',
        text: 'A child\'s education is most successful when the two most important forces in their life — parents and teachers — work as true partners. This collaboration creates a consistent, supportive environment where children feel understood, valued, and motivated to grow.'
      },
      {
        type: 'heading',
        text: 'The Triangle of Success'
      },
      {
        type: 'paragraph',
        text: 'Think of a child\'s development as a triangle: the child at the center, with parents on one side and teachers on the other. When all three sides are connected and communicating, the structure is strong. When one side is missing or disconnected, the child struggles to find balance. At Kids Cosmos School, we actively build this triangle through regular communication, parent events, and open-door policies.'
      },
      {
        type: 'heading',
        text: 'How Parents Can Support Teachers'
      },
      {
        type: 'list',
        items: [
          '📋 Stay informed: Read school newsletters, attend parent meetings, and check your child\'s notebook regularly.',
          '🏠 Reinforce at home: Practice what is taught at school — review letters, numbers, and concepts in daily conversation.',
          '🤝 Trust the teacher: Show confidence in the school\'s approach in front of your child. Children mirror their parents\' attitudes toward school.',
          '💬 Communicate openly: If your child is struggling or something is affecting them at home, let the teacher know early.',
          '🎉 Celebrate school events: Attend sports days, talent shows, and parent days to show your child that education matters to you.'
        ]
      },
      {
        type: 'heading',
        text: 'How Teachers Support Families'
      },
      {
        type: 'paragraph',
        text: 'At Kids Cosmos School, our teachers go beyond the classroom. They observe each child carefully, identify individual strengths and challenges, and communicate with parents proactively. Regular progress updates, one-on-one conversations, and an open-door policy ensure that parents are always informed and involved.'
      },
      {
        type: 'quote',
        text: '"Parents are not just supporters of education — they are co-creators of it."'
      },
      {
        type: 'heading',
        text: 'Building a Culture of Trust'
      },
      {
        type: 'paragraph',
        text: 'Trust is the foundation of parent-teacher partnership. When parents trust teachers with their most precious gift — their child — and teachers honor that trust with care, professionalism, and communication, something extraordinary happens. Children feel safe, supported, and free to learn without anxiety or confusion.'
      },
      {
        type: 'paragraph',
        text: 'At Kids Cosmos School, we are not just educating children — we are building a community of caring adults who believe in every child\'s potential. Together, we can help every child shine. 🌈'
      }
    ]
  },
  {
    id: 4,
    slug: 'creative-learning-art-music-and-play',
    tag: 'Creativity',
    tagColor: '#fce7f3',
    tagTextColor: '#9d174d',
    image: img2,
    title: 'Creative Learning: Art, Music & Play',
    excerpt: 'Creativity is not just a talent — it is a skill that can be nurtured. Learn how art, music, and play shape brilliant young minds.',
    date: 'April 2, 2025',
    readTime: '5 min read',
    author: 'Kids Cosmos Team',
    content: [
      {
        type: 'intro',
        text: 'Creativity is one of the most valuable gifts we can nurture in a child. In a world that increasingly demands innovation, adaptability, and original thinking, giving children rich experiences in art, music, and play is not a luxury — it is a necessity.'
      },
      {
        type: 'heading',
        text: 'Why Creativity Matters in Early Education'
      },
      {
        type: 'paragraph',
        text: 'Creative activities do far more than produce pretty drawings or catchy songs. They build neural pathways, develop problem-solving abilities, strengthen emotional expression, and improve focus and memory. Children who engage regularly in creative activities tend to be better learners, more resilient, and more socially connected.'
      },
      {
        type: 'heading',
        text: 'Art & Craft: More Than Just Drawing'
      },
      {
        type: 'paragraph',
        text: 'When a child picks up a paintbrush, they are not just making art — they are developing fine motor skills, making decisions about color and composition, expressing emotions they may not have words for, and building patience and focus. At Kids Cosmos School, our art and craft sessions use a wide range of materials: watercolors, clay, recycled materials, fabric, and natural items found outdoors.'
      },
      {
        type: 'list',
        items: [
          '✏️ Drawing and sketching develop observation and attention to detail.',
          '🎨 Painting encourages bold decision-making and emotional expression.',
          '✂️ Cutting and collage develop fine motor skills and spatial reasoning.',
          '🏺 Clay and sculpting build three-dimensional thinking and patience.',
          '♻️ Recycled art teaches creativity within constraints and environmental awareness.'
        ]
      },
      {
        type: 'heading',
        text: 'Music & Dance: The Rhythm of Learning'
      },
      {
        type: 'paragraph',
        text: 'Music is a universal language that children respond to instinctively. Research consistently shows that musical training improves mathematical ability, language development, memory, and social skills. At Kids Cosmos School, our music and dance classes are joyful, inclusive celebrations of rhythm, sound, and movement. Every child — regardless of "talent" — is encouraged to sing, move, and express themselves freely.'
      },
      {
        type: 'quote',
        text: '"Music gives a soul to the universe, wings to the mind, flight to the imagination." — Plato'
      },
      {
        type: 'heading',
        text: 'The Power of Imaginative Play'
      },
      {
        type: 'paragraph',
        text: 'When children engage in pretend play — acting as doctors, chefs, superheroes, or teachers — they are developing empathy, language, storytelling, and social negotiation skills. Play is the primary vehicle through which children make sense of the world. It is not a break from learning; it IS learning.'
      },
      {
        type: 'paragraph',
        text: 'At Kids Cosmos School, our play areas, drama corners, and creative studios are carefully designed to spark imagination every single day. We believe that a child who plays freely and creatively today becomes an innovative, confident adult tomorrow. 🎭🎨🎶'
      }
    ]
  },
  {
    id: 5,
    slug: 'school-readiness-tips-for-parents',
    tag: 'Tips',
    tagColor: '#ede9fe',
    tagTextColor: '#6d28d9',
    image: img20,
    title: 'School Readiness: Tips for Parents',
    excerpt: 'Is your child ready for school? These practical tips help parents prepare their little ones for a smooth and happy transition.',
    date: 'April 10, 2025',
    readTime: '4 min read',
    author: 'Kids Cosmos Team',
    content: [
      {
        type: 'intro',
        text: 'Starting school is one of the most significant milestones in a young child\'s life. It can be exciting and a little overwhelming — for children AND parents. With the right preparation, this transition can be smooth, positive, and joyful.'
      },
      {
        type: 'heading',
        text: 'What Does "School Ready" Actually Mean?'
      },
      {
        type: 'paragraph',
        text: 'School readiness is not about whether your child can already read or write. It is about whether they have the foundational skills to engage in a learning environment: the ability to follow simple instructions, interact with other children, manage their emotions, communicate their needs, and handle basic self-care tasks.'
      },
      {
        type: 'heading',
        text: '10 Practical Tips to Prepare Your Child'
      },
      {
        type: 'list',
        items: [
          '📅 Establish a routine: Practice the school schedule — wake time, meals, and bedtime — a few weeks before school starts.',
          '🎒 Practice independence: Encourage your child to dress themselves, use the toilet independently, open their lunchbox, and pack their bag.',
          '📚 Read together daily: Develop a love of books and stories. Visit the library and let your child choose books.',
          '✏️ Practice fine motor skills: Encourage drawing, cutting with scissors, threading beads, and playing with playdough.',
          '🗣️ Build vocabulary: Talk about everything — colours, shapes, feelings, body parts, foods, and daily events.',
          '👫 Arrange playdates: Give your child practice in sharing, taking turns, and making friends before school begins.',
          '🏫 Visit the school: If possible, visit the school campus, meet the teacher, and explore the playground together before day one.',
          '😊 Talk positively about school: Share your own happy school memories. Use excited, positive language when discussing school.',
          '💤 Prioritize sleep: Ensure your child is getting 10–12 hours of sleep each night in the weeks leading up to school.',
          '🤗 Normalize emotions: It is okay to feel nervous. Acknowledge your child\'s feelings and reassure them that school is a safe, fun place.'
        ]
      },
      {
        type: 'quote',
        text: '"Every child deserves a champion — an adult who will never give up on them." — Rita Pierson'
      },
      {
        type: 'heading',
        text: 'On the First Day'
      },
      {
        type: 'paragraph',
        text: 'Keep your goodbye short, warm, and confident. Children pick up on parental anxiety — if you are calm and positive, they will be too. Hand them over to their teacher with a smile and trust that they are in caring hands. At Kids Cosmos School, our teachers are experienced in making every child feel welcome, safe, and excited from Day One.'
      },
      {
        type: 'paragraph',
        text: 'Remember: every child adjusts at their own pace. Some children settle in within days; others take a few weeks. Be patient, celebrate small victories, and stay in communication with your child\'s teacher. You\'ve got this — and so does your child! 🎒✨'
      }
    ]
  },
  {
    id: 6,
    slug: 'emotional-and-social-development-in-young-children',
    tag: 'Development',
    tagColor: '#ffedd5',
    tagTextColor: '#c2410c',
    image: img12,
    title: 'Emotional & Social Development in Young Children',
    excerpt: 'Understanding and supporting your child\'s emotional and social growth is just as important as academic learning.',
    date: 'April 18, 2025',
    readTime: '6 min read',
    author: 'Kids Cosmos Team',
    content: [
      {
        type: 'intro',
        text: 'Academic knowledge is important — but emotional intelligence and social skills are the true predictors of a child\'s long-term success and happiness. Children who can understand their emotions, empathize with others, and navigate social situations grow into resilient, compassionate, and effective human beings.'
      },
      {
        type: 'heading',
        text: 'What Is Social-Emotional Development?'
      },
      {
        type: 'paragraph',
        text: 'Social-emotional development refers to how children understand their own feelings, develop empathy for others, form relationships, and learn to manage their behaviour in different situations. It begins at birth and is shaped by every interaction a child has with caregivers, peers, and the environment around them.'
      },
      {
        type: 'heading',
        text: 'Key Social-Emotional Milestones'
      },
      {
        type: 'list',
        items: [
          '👶 Ages 2–3: Begin to express emotions like joy, anger, fear, and sadness. Start parallel play alongside (not yet with) other children.',
          '🧒 Ages 3–4: Begin cooperative play, sharing toys, and taking turns. Show empathy — comfort a crying friend.',
          '🧒 Ages 4–5: Form friendships, understand simple rules, and begin to resolve conflicts with words.',
          '👦 Ages 5–6: Develop a clearer sense of self, pride in achievements, and the ability to follow complex social rules.',
          '🏫 School age: Understand fairness, develop loyalty to friends, and begin to navigate peer pressure.'
        ]
      },
      {
        type: 'heading',
        text: 'How to Support Emotional Development at Home'
      },
      {
        type: 'paragraph',
        text: 'Emotional intelligence starts with emotional vocabulary. When children can NAME their feelings, they can begin to MANAGE them. Use a "feelings chart" at home — a simple poster with illustrated emotions — and check in with your child each day: "How are you feeling today? Can you show me on the chart?"'
      },
      {
        type: 'list',
        items: [
          '❤️ Validate emotions: Never dismiss feelings. "I understand you\'re feeling frustrated. That\'s okay. Let\'s figure it out together."',
          '📖 Use stories: Books are a powerful way to explore emotions safely. Discuss characters\' feelings and choices.',
          '🎭 Role-play scenarios: Practice social situations through play — what do you do if someone takes your toy? What if a friend is sad?',
          '🌿 Model emotional regulation: Let your child see you manage your own emotions calmly and constructively.',
          '🤗 Build secure attachment: Consistent love, physical affection, and responsiveness are the foundation of emotional security.'
        ]
      },
      {
        type: 'quote',
        text: '"It is easier to build strong children than to repair broken adults." — Frederick Douglass'
      },
      {
        type: 'heading',
        text: 'Our Approach at Kids Cosmos School'
      },
      {
        type: 'paragraph',
        text: 'At Kids Cosmos School, social-emotional learning (SEL) is woven into every part of our day. From morning circle time where children share how they are feeling, to cooperative group projects, conflict resolution conversations, and celebration of every child\'s unique personality — we prioritize the whole child, not just the academic child.'
      },
      {
        type: 'paragraph',
        text: 'We believe that a child who feels emotionally safe is a child who is free to learn, explore, take risks, make mistakes, and grow. That is the heart of everything we do at Kids Cosmos School. 💛'
      }
    ]
  }
];